import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "../config";
import {
  ALL_FIELDS,
  VOICE_FIELDS,
  STORY_FIELDS,
  CRITERIA_LABELS,
  voiceScore,
  storyScore,
  totalScore,
  isComplete,
  computeComposite,
  avgCriterion,
} from "../utils/scoreComputation";

const ADMIN_PASSWORD = "kathakala2026";

// Storytellers explicitly ineligible for the shortlist beyond judges themselves
// (hosts and other roles that disqualify them). Compared word-order-agnostic.
const INELIGIBLE_STORYTELLERS_TELUGU = [
  "భాస్కర్ రాయవరం", // Bhaskar (host)
  "చుండూరి మృణాళిని", // Chunduri Mrunalini
];

const CATEGORY_OPTIONS = [
  { value: "galakala", label: "గళకళ (Voice Art)" },
  { value: "kalam_galam", label: "కలం-గళం (Writer-Voice Art)" },
  { value: "toli_kathanam_galam", label: "తొలి కథనం-గళం (Debut Voice Art)" },
  {
    value: "toli_kathanam_kalam",
    label: "తొలి కథనం-కలం (Debut Writer-Voice)",
  },
];

const AWARD_SECTIONS = [
  {
    category: "galakala",
    title: "గళకళ (Voice Art)",
    borderColor: "border-amber-500",
  },
  {
    category: "kalam_galam",
    title: "కలం-గళం (Writer-Voice Art)",
    borderColor: "border-blue-500",
  },
  {
    category: "toli_kathanam_galam",
    title: "తొలి కథనం-గళం (Debut Voice Art)",
    borderColor: "border-amber-400",
  },
  {
    category: "toli_kathanam_kalam",
    title: "తొలి కథనం-కలం (Debut Writer-Voice)",
    borderColor: "border-blue-400",
  },
];

const PuraskaraluAdmin = () => {
  // Auth
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Layout
  const [activeTab, setActiveTab] = useState("shortlist");

  // Seasons
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState("");
  const [newSeason, setNewSeason] = useState({
    name: "",
    date_from: "",
    date_to: "",
    status: "open",
  });

  // Shortlist data
  const [shortlist, setShortlist] = useState([]);
  const [allPerformances, setAllPerformances] = useState([]);
  const [storiesMap, setStoriesMap] = useState({});
  const [writersMap, setWritersMap] = useState({});
  const [episodesMap, setEpisodesMap] = useState({});
  const [storytellersMap, setStorytellersMap] = useState({});
  const [addCategory, setAddCategory] = useState("galakala");

  // Scores data
  const [judgeScores, setJudgeScores] = useState([]);
  const [judges, setJudges] = useState([]);
  const [expandedPerfId, setExpandedPerfId] = useState(null);

  // Auto-shortlist preview
  const [autoPreview, setAutoPreview] = useState(null);
  const [autoCategory, setAutoCategory] = useState("galakala");

  // Results tab: judge selector ("all" = averaged, "ai" = Kathakala Agent, or storyteller_id)
  const [resultsJudge, setResultsJudge] = useState("all");

  // Inline start-time editing
  const [editingStartTime, setEditingStartTime] = useState(null); // performance_id
  const [startTimeValue, setStartTimeValue] = useState("");

  // YouTube player for shortlist tab
  const [playerVideoId, setPlayerVideoId] = useState(null);
  const [playerStartSec, setPlayerStartSec] = useState(0);

  // Shortlist category sub-tab
  const [shortlistCatTab, setShortlistCatTab] = useState("galakala");

  // Agent ratings lock
  const [agentLockStatus, setAgentLockStatus] = useState(null); // null | 'locking' | 'locked'
  const [agentLockCount, setAgentLockCount] = useState(0);

  // UI
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // ---------- Auth ----------
  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError("");
    } else {
      setPasswordError("Incorrect password.");
    }
  };

  // ---------- Fetch seasons ----------
  const fetchSeasons = useCallback(async () => {
    const { data, error } = await supabase
      .from("jeedigunta_seasons")
      .select("*")
      .order("season_id", { ascending: false });
    if (error) {
      console.error("Error fetching seasons:", error);
      return;
    }
    setSeasons(data || []);
    if (data && data.length > 0 && !selectedSeasonId) {
      setSelectedSeasonId(String(data[0].season_id));
    }
  }, [selectedSeasonId]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSeasons();
    }
  }, [isAuthenticated, fetchSeasons]);

  // ---------- Create season ----------
  const handleCreateSeason = async () => {
    if (!newSeason.name || !newSeason.date_from || !newSeason.date_to) {
      setErrorMsg("All season fields are required.");
      return;
    }
    setErrorMsg("");
    const { data, error } = await supabase
      .from("jeedigunta_seasons")
      .insert({
        name: newSeason.name,
        date_from: newSeason.date_from,
        date_to: newSeason.date_to,
        status: newSeason.status,
      })
      .select();
    if (error) {
      setErrorMsg("Error creating season: " + error.message);
      return;
    }
    setNewSeason({ name: "", date_from: "", date_to: "", status: "open" });
    setSuccessMsg("Season created.");
    setTimeout(() => setSuccessMsg(""), 3000);
    await fetchSeasons();
    if (data && data[0]) {
      setSelectedSeasonId(String(data[0].season_id));
    }
  };

  // ---------- Change season status ----------
  const handleChangeStatus = async (newStatus) => {
    if (!selectedSeasonId) return;
    const { error } = await supabase
      .from("jeedigunta_seasons")
      .update({ status: newStatus })
      .eq("season_id", parseInt(selectedSeasonId));
    if (error) {
      setErrorMsg("Error updating status: " + error.message);
      return;
    }
    setSuccessMsg(`Status changed to "${newStatus}".`);
    setTimeout(() => setSuccessMsg(""), 3000);
    await fetchSeasons();
  };

  const selectedSeason = useMemo(
    () => seasons.find((s) => String(s.season_id) === selectedSeasonId),
    [seasons, selectedSeasonId]
  );

  // ---------- Fetch enrichment maps (stories, writers, episodes, storytellers) ----------
  const enrichPerformances = useCallback(async (perfIds, perfsData) => {
    if (!perfsData || perfsData.length === 0) return;

    const storyIds = [
      ...new Set(perfsData.map((p) => p.story_id).filter(Boolean)),
    ];
    const episodeIds = [
      ...new Set(perfsData.map((p) => p.episode_id).filter(Boolean)),
    ];
    const storytellerIds = [
      ...new Set(perfsData.map((p) => p.storyteller_id).filter(Boolean)),
    ];

    const [storiesRes, episodesRes, storytellersRes] = await Promise.all([
      storyIds.length > 0
        ? supabase.from("stories").select("*").in("story_id", storyIds)
        : { data: [] },
      episodeIds.length > 0
        ? supabase.from("episodes").select("*").in("episode_id", episodeIds)
        : { data: [] },
      storytellerIds.length > 0
        ? supabase
            .from("storytellers")
            .select("storyteller_id, storyteller_telugu, storyteller_english")
            .in("storyteller_id", storytellerIds)
        : { data: [] },
    ]);

    const sMap = {};
    (storiesRes.data || []).forEach((s) => {
      sMap[s.story_id] = s;
    });

    const writerIds = [
      ...new Set(
        Object.values(sMap)
          .map((s) => s.writer_id)
          .filter(Boolean)
      ),
    ];
    let wRes = { data: [] };
    if (writerIds.length > 0) {
      wRes = await supabase
        .from("writers")
        .select("*")
        .in("writer_id", writerIds);
    }
    const wMap = {};
    (wRes.data || []).forEach((w) => {
      wMap[w.writer_id] = w;
    });

    const eMap = {};
    (episodesRes.data || []).forEach((e) => {
      eMap[e.episode_id] = e;
    });

    const stMap = {};
    (storytellersRes.data || []).forEach((st) => {
      stMap[st.storyteller_id] = st;
    });

    setStoriesMap(sMap);
    setWritersMap(wMap);
    setEpisodesMap(eMap);
    setStorytellersMap(stMap);
  }, []);

  // ---------- Fetch shortlist data ----------
  const fetchShortlistData = useCallback(async () => {
    if (!selectedSeasonId) return;
    setIsLoading(true);
    setErrorMsg("");
    try {
      // Fetch shortlist entries + judges list (judges are excluded from the extended list)
      const [{ data: slData, error: slError }, { data: judgesList }] = await Promise.all([
        supabase
          .from("jeedigunta_shortlist")
          .select("*")
          .eq("season_id", parseInt(selectedSeasonId)),
        supabase
          .from("storytellers")
          .select("storyteller_id, storyteller_telugu, storyteller_english, judge_sequence")
          .eq("is_judge", true),
      ]);
      if (slError) throw slError;
      setShortlist(slData || []);
      setJudges(judgesList || []);

      const perfIds = (slData || []).map((s) => s.performance_id);

      // Fetch performances
      let perfsData = [];
      if (perfIds.length > 0) {
        const { data: pData, error: pError } = await supabase
          .from("performances")
          .select("*")
          .in("performance_id", perfIds);
        if (pError) throw pError;
        perfsData = pData || [];
      }

      // Fetch all performances for the "Add" browser
      const season = seasons.find(
        (s) => String(s.season_id) === selectedSeasonId
      );
      if (season) {
        const { data: allPerfs } = await supabase
          .from("performances")
          .select("*")
          .order("performance_id", { ascending: true });
        setAllPerformances(allPerfs || []);
        // Enrich with all performances combined
        const allPerfData = [...perfsData];
        (allPerfs || []).forEach((p) => {
          if (!allPerfData.find((x) => x.performance_id === p.performance_id)) {
            allPerfData.push(p);
          }
        });
        await enrichPerformances(
          allPerfData.map((p) => p.performance_id),
          allPerfData
        );
      } else {
        await enrichPerformances(perfIds, perfsData);
      }
    } catch (err) {
      console.error("Error fetching shortlist:", err);
      setErrorMsg(err?.message || "Failed to load shortlist data.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedSeasonId, seasons, enrichPerformances]);

  // ---------- Fetch scores data ----------
  const fetchScoresData = useCallback(async () => {
    if (!selectedSeasonId) return;
    setIsLoading(true);
    setErrorMsg("");
    try {
      const [scoresRes, judgesRes, shortlistRes] = await Promise.all([
        supabase
          .from("jeedigunta_judge_scores")
          .select("*")
          .eq("season_id", parseInt(selectedSeasonId)),
        supabase
          .from("storytellers")
          .select("storyteller_id, storyteller_telugu, storyteller_english, judge_sequence")
          .eq("is_judge", true),
        supabase
          .from("jeedigunta_shortlist")
          .select("*")
          .eq("season_id", parseInt(selectedSeasonId)),
      ]);

      if (scoresRes.error) throw scoresRes.error;
      if (judgesRes.error) throw judgesRes.error;
      if (shortlistRes.error) throw shortlistRes.error;

      const scores = scoresRes.data || [];
      const judgesList = judgesRes.data || [];
      if (typeof window !== 'undefined') {
        const byJudge = {};
        scores.forEach(s => { byJudge[s.judge_storyteller_id] = (byJudge[s.judge_storyteller_id] || 0) + 1; });
        window.__fetchTrace = { scoresLen: scores.length, judgesLen: judgesList.length, byJudge, firstScoreId: scores[0]?.score_id, lastScoreId: scores[scores.length-1]?.score_id };
      }
      setJudgeScores(scores);
      setJudges(judgesList);
      setShortlist(shortlistRes.data || []);

      // Check if agent ratings are already locked
      const agentJudge = judgesList.find(
        (j) => (j.storyteller_english || "").toLowerCase() === "kathakala agent"
      );
      if (agentJudge) {
        const agentScoreCount = scores.filter(
          (s) => s.judge_storyteller_id === agentJudge.storyteller_id
        ).length;
        if (agentScoreCount > 0) {
          setAgentLockStatus("locked");
          setAgentLockCount(agentScoreCount);
        } else {
          setAgentLockStatus(null);
          setAgentLockCount(0);
        }
      } else {
        setAgentLockStatus(null);
        setAgentLockCount(0);
      }

      // Enrich shortlisted performances
      const perfIds = (shortlistRes.data || []).map((s) => s.performance_id);
      if (perfIds.length > 0) {
        const { data: perfsData } = await supabase
          .from("performances")
          .select("*")
          .in("performance_id", perfIds);
        setAllPerformances(perfsData || []);
        await enrichPerformances(perfIds, perfsData || []);
      }
    } catch (err) {
      console.error("Error fetching scores:", err);
      setErrorMsg(err?.message || "Failed to load scores data.");
    } finally {
      setIsLoading(false);
    }
  }, [selectedSeasonId, enrichPerformances]);

  // ---------- Fetch data on tab/season change ----------
  useEffect(() => {
    if (!isAuthenticated || !selectedSeasonId) return;
    if (activeTab === "dashboard") {
      fetchScoresData(); // Dashboard needs judges + scores + shortlist
    } else if (activeTab === "shortlist") {
      fetchShortlistData();
    } else if (activeTab === "scores") {
      fetchScoresData();
    } else if (activeTab === "results") {
      fetchScoresData(); // Results tab also needs scores + shortlist
    } else if (activeTab === "judges") {
      fetchScoresData(); // Judges tab needs scores + shortlist + performances
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, selectedSeasonId, activeTab]);

  // ---------- Shortlist actions ----------
  const handleAddToShortlist = async (performanceId) => {
    if (!selectedSeasonId || !performanceId) return;
    setErrorMsg("");
    const { error } = await supabase.from("jeedigunta_shortlist").insert({
      season_id: parseInt(selectedSeasonId),
      performance_id: performanceId,
    });
    if (error) {
      if (error.code === "23505") {
        setErrorMsg("This performance is already shortlisted in this category.");
      } else {
        setErrorMsg("Error adding to shortlist: " + error.message);
      }
      return;
    }
    setSuccessMsg("Added to shortlist.");
    setTimeout(() => setSuccessMsg(""), 3000);
    await fetchShortlistData();
  };

  const handleRemoveFromShortlist = async (shortlistId) => {
    if (!window.confirm("Remove this performance from the shortlist?")) return;
    const { error } = await supabase
      .from("jeedigunta_shortlist")
      .delete()
      .eq("shortlist_id", shortlistId);
    if (error) {
      setErrorMsg("Error removing: " + error.message);
      return;
    }
    setSuccessMsg("Removed from shortlist.");
    setTimeout(() => setSuccessMsg(""), 3000);
    await fetchShortlistData();
  };

  // ---------- Auto-shortlist ----------
  const handleAutoShortlistPreview = async () => {
    if (!selectedSeason) return;
    setErrorMsg("");
    setIsLoading(true);
    try {
      // Fetch performances in season date range via episodes
      const { data: episodes } = await supabase
        .from("episodes")
        .select("episode_id, publish_date")
        .gte("publish_date", selectedSeason.date_from)
        .lte("publish_date", selectedSeason.date_to);

      if (!episodes || episodes.length === 0) {
        setErrorMsg("No episodes found in the season date range.");
        setIsLoading(false);
        return;
      }

      const episodeIds = episodes.map((e) => e.episode_id);
      const { data: perfs } = await supabase
        .from("performances")
        .select("*")
        .in("episode_id", episodeIds)
        .not("rating_story", "is", null)
        .order("rating_story", { ascending: false });

      if (!perfs || perfs.length === 0) {
        setErrorMsg("No rated performances found in date range.");
        setIsLoading(false);
        return;
      }

      // Top-10 with ties
      const sorted = [...perfs].sort(
        (a, b) => (b.rating_story || 0) - (a.rating_story || 0)
      );
      let cutoffRating = null;
      const top = [];
      for (const p of sorted) {
        if (top.length < 10) {
          top.push(p);
          cutoffRating = p.rating_story;
        } else if (p.rating_story === cutoffRating) {
          top.push(p);
        } else {
          break;
        }
      }

      // Enrich preview performances
      await enrichPerformances(
        top.map((p) => p.performance_id),
        top
      );

      setAutoPreview(top);
    } catch (err) {
      console.error("Auto-shortlist error:", err);
      setErrorMsg(err?.message || "Failed to compute auto-shortlist.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmAutoShortlist = async () => {
    if (!autoPreview || !selectedSeasonId) return;
    setErrorMsg("");
    const rows = autoPreview.map((p) => ({
      season_id: parseInt(selectedSeasonId),
      performance_id: p.performance_id,
    }));
    const { error } = await supabase
      .from("jeedigunta_shortlist")
      .upsert(rows, { onConflict: "season_id,performance_id" });
    if (error) {
      setErrorMsg("Error saving auto-shortlist: " + error.message);
      return;
    }
    setSuccessMsg(`Added ${rows.length} performances to shortlist.`);
    setTimeout(() => setSuccessMsg(""), 3000);
    setAutoPreview(null);
    await fetchShortlistData();
  };

  // ---------- CSV Export ----------
  const handleExportCSV = () => {
    const uniquePerfIds = [...new Set(shortlist.map((s) => s.performance_id))];
    const header = [
      "Story",
      "Writer",
      "Storyteller",
      "Category",
      ...judges.map(
        (j) => j.storyteller_telugu || j.storyteller_english || j.storyteller_id
      ),
      "Avg Total",
    ];

    const rows = uniquePerfIds.map((perfId) => {
      const perf = allPerformances.find((p) => p.performance_id === perfId);
      const story = perf ? storiesMap[perf.story_id] : null;
      const writer = story ? writersMap[story.writer_id] : null;
      const storyteller = perf ? storytellersMap[perf.storyteller_id] : null;
      const storyTitle = story?.title_telugu || perf?.storyteller_name_telugu || "";
      const writerName = writer?.name_telugu || "";
      const storytellerName =
        storyteller?.storyteller_telugu ||
        perf?.storyteller_name_telugu ||
        "";

      const judgeTotals = judges.map((j) => {
        const scores = judgeScores.filter(
          (s) =>
            s.performance_id === perfId && s.judge_storyteller_id === j.storyteller_id
        );
        if (scores.length === 0) return "--";
        const row = scores[0];
        const t = totalScore(row);
        return t !== null ? t.toFixed(1) : "--";
      });

      const allJudgeScores = judgeScores.filter(
        (s) => s.performance_id === perfId
      );
      const completeTotals = allJudgeScores
        .map((r) => totalScore(r))
        .filter((t) => t !== null);
      const avgTotal =
        completeTotals.length > 0
          ? (
              completeTotals.reduce((a, b) => a + b, 0) / completeTotals.length
            ).toFixed(2)
          : "--";

      return [
        storyTitle,
        writerName,
        storytellerName,
        perf?.category || "",
        ...judgeTotals,
        avgTotal,
      ];
    });

    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `puraskaralu-scores-${selectedSeasonId}.csv`;
    link.click();
  };

  // ---------- Save start time ----------
  const timeToSec = (t) => {
    if (!t) return null;
    const parts = t.split(":").map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return null;
  };

  const handleSaveStartTime = async (perfId) => {
    const sec = timeToSec(startTimeValue);
    const { error } = await supabase
      .from("performances")
      .update({ story_start_time: startTimeValue || null, story_start_sec: sec })
      .eq("performance_id", perfId);
    if (error) {
      setErrorMsg("Error saving start time: " + error.message);
    } else {
      setSuccessMsg("Start time updated.");
      setTimeout(() => setSuccessMsg(""), 2000);
      // Update local state
      setAllPerformances((prev) =>
        prev.map((p) =>
          p.performance_id === perfId
            ? { ...p, story_start_time: startTimeValue || null, story_start_sec: sec }
            : p
        )
      );
    }
    setEditingStartTime(null);
    setStartTimeValue("");
  };

  // ---------- Lock Agent Ratings ----------
  const handleLockAgentRatings = async () => {
    if (!selectedSeasonId || shortlist.length === 0) return;
    if (!window.confirm(
      "This will save the current AI ratings as official Kathakala Agent judge scores for all shortlisted performances. Existing agent scores will be overwritten. Continue?"
    )) return;

    setAgentLockStatus("locking");
    setErrorMsg("");
    try {
      // Step 1: Find or create the "Kathakala Agent" storyteller
      const { data: existing } = await supabase
        .from("storytellers")
        .select("storyteller_id")
        .eq("storyteller_english", "Kathakala Agent")
        .eq("is_judge", true)
        .limit(1);

      let agentJudgeId;
      if (existing && existing.length > 0) {
        agentJudgeId = existing[0].storyteller_id;
      } else {
        const { data: created, error: createErr } = await supabase
          .from("storytellers")
          .insert({
            storyteller_english: "Kathakala Agent",
            storyteller_telugu: "కథాకళ ఏజెంట్",
            is_judge: true,
          })
          .select("storyteller_id");
        if (createErr) throw createErr;
        agentJudgeId = created[0].storyteller_id;
      }

      // Step 2: Build upsert rows from AI scores in performances table
      const perfIds = [...new Set(shortlist.map((s) => s.performance_id))];
      const allFields = [...VOICE_FIELDS, ...STORY_FIELDS];
      const upsertRows = [];
      let skipped = 0;

      for (const perfId of perfIds) {
        const perf = allPerformances.find((p) => p.performance_id === perfId);
        if (!perf || perf.rating_story == null) {
          skipped++;
          continue;
        }

        const row = {
          season_id: parseInt(selectedSeasonId),
          performance_id: perfId,
          judge_storyteller_id: agentJudgeId,
          updated_at: new Date().toISOString(),
        };
        allFields.forEach((f) => {
          const val = perf[f];
          row[f] = val != null ? parseFloat(val) : null;
        });
        upsertRows.push(row);
      }

      if (upsertRows.length === 0) {
        setErrorMsg("No performances have AI ratings to lock.");
        setAgentLockStatus(null);
        return;
      }

      // Step 3: Upsert all agent scores
      const { error: upsertErr } = await supabase
        .from("jeedigunta_judge_scores")
        .upsert(upsertRows, {
          onConflict: "season_id,performance_id,judge_storyteller_id",
        });
      if (upsertErr) throw upsertErr;

      setAgentLockCount(upsertRows.length);
      setAgentLockStatus("locked");
      setSuccessMsg(
        `Agent ratings locked for ${upsertRows.length} performances` +
        (skipped > 0 ? ` (${skipped} skipped — no AI ratings)` : "") +
        "."
      );
      setTimeout(() => setSuccessMsg(""), 5000);

      // Refresh scores data
      await fetchScoresData();
    } catch (err) {
      console.error("Error locking agent ratings:", err);
      setErrorMsg("Error locking agent ratings: " + (err?.message || err));
      setAgentLockStatus(null);
    }
  };

  // ---------- Helper: get display info for a performance ----------
  const getPerfDisplay = (perfId) => {
    const perf = allPerformances.find((p) => p.performance_id === perfId);
    if (!perf) return { storyTitle: "", writerName: "", storytellerName: "" };
    const story = storiesMap[perf.story_id];
    const writer = story ? writersMap[story.writer_id] : null;
    const storyteller = storytellersMap[perf.storyteller_id];

    return {
      storyTitle: story?.title_telugu || story?.title_english || "",
      writerName: writer?.name_telugu || writer?.name_english || "",
      storytellerName:
        storyteller?.storyteller_telugu ||
        perf?.storyteller_name_telugu ||
        "",
      ratingStory: perf.rating_story,
    };
  };

  // ---------- Results computation ----------
  // Build Kathakala Agent virtual scores from performances' AI fields
  const aiScoresMap = useMemo(() => {
    const map = {};
    allPerformances.forEach((p) => {
      // Only if at least rating_story exists
      if (p.rating_story == null) return;
      map[p.performance_id] = {
        shruti: p.shruti,
        modulation: p.modulation,
        uccharana: p.uccharana,
        gati: p.gati,
        rating_story: p.rating_story,
        rating_writer: p.rating_writer,
        readability: p.readability,
        vastu: p.vastu,
        shilpam: p.shilpam,
        innovation: p.innovation,
      };
    });
    return map;
  }, [allPerformances]);

  const resultsData = useMemo(() => {
    if (activeTab !== "results" || shortlist.length === 0) return {};

    const result = {};
    for (const section of AWARD_SECTIONS) {
      // Get performances matching this category from the performances table
      const catPerfIds = [
        ...new Set(
          shortlist
            .map((s) => s.performance_id)
            .filter((pid) => {
              const perf = allPerformances.find((p) => p.performance_id === pid);
              if (!perf) return false;
              // Map performance.category (Telugu) to section.category (English)
              const catMap = {
                "గళకళ": "galakala",
                "కలం-గళం": "kalam_galam",
                "తొలి కథనం - గళం": "toli_kathanam_galam",
                "తొలి కథనం - కలం": "toli_kathanam_kalam",
              };
              return catMap[perf.category] === section.category;
            })
        ),
      ];

      const ranked = catPerfIds.map((perfId) => {
        const display = getPerfDisplay(perfId);
        const slEntry = shortlist.find((s) => s.performance_id === perfId);
        const perf = allPerformances.find((p) => p.performance_id === perfId);
        const ep = perf ? episodesMap[perf.episode_id] : null;
        let vs, ss, composite, judgeCount, criteria = {};

        if (resultsJudge === "ai") {
          // Kathakala Agent: use AI scores from performance
          const aiRow = aiScoresMap[perfId];
          if (aiRow && isComplete(aiRow)) {
            vs = voiceScore(aiRow);
            ss = storyScore(aiRow);
            if (section.category === "galakala" || section.category === "toli_kathanam_galam") {
              composite = 0.80 * (aiRow.shruti + aiRow.modulation + aiRow.uccharana + aiRow.gati) + 0.20 * (aiRow.rating_story + aiRow.rating_writer + aiRow.readability + aiRow.vastu + aiRow.shilpam + aiRow.innovation);
            } else {
              composite = 0.75 * (aiRow.rating_story + aiRow.rating_writer + aiRow.readability + aiRow.vastu + aiRow.shilpam + aiRow.innovation) + 0.25 * (aiRow.shruti + aiRow.modulation + aiRow.uccharana + aiRow.gati);
            }
            criteria = { ...aiRow };
            judgeCount = 1;
          } else {
            vs = null; ss = null; composite = null; judgeCount = 0;
          }
        } else if (resultsJudge === "all") {
          // All judges averaged criteria, then SUM-based composite
          const scores = judgeScores.filter((s) => s.performance_id === perfId);
          const comp = computeComposite(scores, section.category);
          vs = comp.voiceScore;
          ss = comp.storyScore;
          criteria = comp.avgCriteria || {};
          judgeCount = scores.filter(isComplete).length;
          // Recompute composite using SUM-based formula (not avg-based)
          const ac = criteria;
          const vSum = (parseFloat(ac.shruti)||0) + (parseFloat(ac.modulation)||0) + (parseFloat(ac.uccharana)||0) + (parseFloat(ac.gati)||0);
          const sSum = (parseFloat(ac.rating_story)||0) + (parseFloat(ac.rating_writer)||0) + (parseFloat(ac.readability)||0) + (parseFloat(ac.vastu)||0) + (parseFloat(ac.shilpam)||0) + (parseFloat(ac.innovation)||0);
          if (vs !== null && ss !== null) {
            if (section.category === "galakala" || section.category === "toli_kathanam_galam") {
              composite = 0.80 * vSum + 0.20 * sSum;
            } else {
              composite = 0.75 * sSum + 0.25 * vSum;
            }
          } else {
            composite = comp.composite;
          }
        } else {
          // Specific judge
          const scores = judgeScores.filter(
            (s) => s.performance_id === perfId && s.judge_storyteller_id === resultsJudge
          );
          if (scores.length > 0 && isComplete(scores[0])) {
            const row = scores[0];
            vs = voiceScore(row);
            ss = storyScore(row);
            if (section.category === "galakala" || section.category === "toli_kathanam_galam") {
              composite = 0.80 * (row.shruti + row.modulation + row.uccharana + row.gati) + 0.20 * (row.rating_story + row.rating_writer + row.readability + row.vastu + row.shilpam + row.innovation);
            } else {
              composite = 0.75 * (row.rating_story + row.rating_writer + row.readability + row.vastu + row.shilpam + row.innovation) + 0.25 * (row.shruti + row.modulation + row.uccharana + row.gati);
            }
            criteria = {
              shruti: row.shruti, modulation: row.modulation, uccharana: row.uccharana, gati: row.gati,
              rating_story: row.rating_story, rating_writer: row.rating_writer, readability: row.readability,
              vastu: row.vastu, shilpam: row.shilpam, innovation: row.innovation,
            };
            judgeCount = 1;
          } else {
            vs = null; ss = null; composite = null; judgeCount = 0;
          }
        }

        return {
          ...slEntry,
          performance_id: perfId,
          ...display,
          voiceScoreVal: vs,
          storyScoreVal: ss,
          composite,
          judgeCount,
          criteria,
          videoId: ep?.video_id || null,
          startSec: perf?.story_start_sec || 0,
        };
      });

      ranked.sort((a, b) => (b.composite || 0) - (a.composite || 0));
      result[section.category] = ranked;
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, shortlist, judgeScores, storiesMap, writersMap, storytellersMap, allPerformances, resultsJudge, aiScoresMap]);

  // ---------- Shortlist tab: ranked by category using agent AI scores ----------
  const shortlistByCategory = useMemo(() => {
    if (shortlist.length === 0 || allPerformances.length === 0) return {};
    const catMap = {
      "గళకళ": "galakala",
      "కలం-గళం": "kalam_galam",
      "తొలి కథనం - గళం": "toli_kathanam_galam",
      "తొలి కథనం - కలం": "toli_kathanam_kalam",
    };
    const result = {};
    for (const section of AWARD_SECTIONS) {
      const entries = shortlist
        .map((sl) => {
          const perf = allPerformances.find((p) => p.performance_id === sl.performance_id);
          if (!perf) return null;
          if (catMap[perf.category] !== section.category) return null;
          const display = getPerfDisplay(sl.performance_id);
          const ep = episodesMap[perf.episode_id];
          // Agent AI scores from performance
          const c = {
            shruti: perf.shruti, modulation: perf.modulation, uccharana: perf.uccharana, gati: perf.gati,
            rating_story: perf.rating_story, rating_writer: perf.rating_writer, readability: perf.readability,
            vastu: perf.vastu, shilpam: perf.shilpam, innovation: perf.innovation,
          };
          const hasScores = c.shruti != null && c.rating_story != null;
          const vSum = hasScores ? (parseFloat(c.shruti)||0) + (parseFloat(c.modulation)||0) + (parseFloat(c.uccharana)||0) + (parseFloat(c.gati)||0) : null;
          const sSum = hasScores ? (parseFloat(c.rating_story)||0) + (parseFloat(c.rating_writer)||0) + (parseFloat(c.readability)||0) + (parseFloat(c.vastu)||0) + (parseFloat(c.shilpam)||0) + (parseFloat(c.innovation)||0) : null;
          const vAvg = vSum != null ? vSum / 4 : null;
          const sAvg = sSum != null ? sSum / 6 : null;
          const isVoiceCat = section.category === "galakala" || section.category === "toli_kathanam_galam";
          const composite = (vSum != null && sSum != null) ? (isVoiceCat ? 0.80 * vSum + 0.20 * sSum : 0.75 * sSum + 0.25 * vSum) : null;
          return {
            ...sl,
            performance_id: sl.performance_id,
            ...display,
            criteria: c,
            vAvg,
            sAvg,
            composite,
            videoId: ep?.video_id || null,
            startSec: perf?.story_start_sec || 0,
            startTime: perf?.story_start_time || "",
          };
        })
        .filter(Boolean);
      entries.sort((a, b) => (b.composite || 0) - (a.composite || 0));
      result[section.category] = entries;
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlist, allPerformances, storiesMap, writersMap, storytellersMap, episodesMap]);

  // ---------- Extended list tab: next 50 per category by agent score (not shortlisted) ----------
  // Applies the same tie-breaker as the shortlist ranking:
  //   composite DESC → V-sum (voice cats) or S-sum (writing cats) DESC → modulation or innovation DESC
  const extendedByCategory = useMemo(() => {
    if (allPerformances.length === 0) return {};
    const season = seasons.find(
      (s) => String(s.season_id) === selectedSeasonId
    );
    if (!season) return {};
    const catMap = {
      "గళకళ": "galakala",
      "కలం-గళం": "kalam_galam",
      "తొలి కథనం - గళం": "toli_kathanam_galam",
      "తొలి కథనం - కలం": "toli_kathanam_kalam",
    };
    const shortlistedIds = new Set(shortlist.map((s) => s.performance_id));
    const judgeIds = new Set(judges.map((j) => j.storyteller_id));
    // Word-order-agnostic name key (performances store names differently — e.g.
    // "రాయవరం రఘునందన్" vs storytellers "రఘునందన్ రాయవరం").
    const nameKey = (raw) =>
      (raw || "")
        .replace(/\s+/g, " ")
        .trim()
        .split(" ")
        .filter(Boolean)
        .sort()
        .join(" ");
    const excludedNameKeys = new Set([
      ...judges.map((j) => nameKey(j.storyteller_telugu)),
      ...INELIGIBLE_STORYTELLERS_TELUGU.map(nameKey),
    ].filter(Boolean));
    const result = {};
    for (const section of AWARD_SECTIONS) {
      const isVoiceCat =
        section.category === "galakala" ||
        section.category === "toli_kathanam_galam";
      const entries = allPerformances
        .filter((p) => {
          if (shortlistedIds.has(p.performance_id)) return false;
          if (catMap[p.category] !== section.category) return false;
          // Only include performances in episodes within the season date range
          const ep = episodesMap[p.episode_id];
          if (!ep || !ep.publish_date) return false;
          if (
            ep.publish_date < season.date_from ||
            ep.publish_date > season.date_to
          )
            return false;
          // All 10 agent criteria must be present
          const cols = [
            "shruti", "modulation", "uccharana", "gati",
            "rating_story", "rating_writer", "readability",
            "vastu", "shilpam", "innovation",
          ];
          if (cols.some((c) => p[c] == null)) return false;
          // Exclude judge storytellers (ineligible for the shortlist)
          if (p.storyteller_id && judgeIds.has(p.storyteller_id)) return false;
          const nk = nameKey(p.storyteller_name_telugu);
          if (nk && excludedNameKeys.has(nk)) return false;
          return true;
        })
        .map((p) => {
          const display = getPerfDisplay(p.performance_id);
          const ep = episodesMap[p.episode_id];
          const c = {
            shruti: p.shruti, modulation: p.modulation, uccharana: p.uccharana, gati: p.gati,
            rating_story: p.rating_story, rating_writer: p.rating_writer, readability: p.readability,
            vastu: p.vastu, shilpam: p.shilpam, innovation: p.innovation,
          };
          const vSum = (parseFloat(c.shruti)||0) + (parseFloat(c.modulation)||0) + (parseFloat(c.uccharana)||0) + (parseFloat(c.gati)||0);
          const sSum = (parseFloat(c.rating_story)||0) + (parseFloat(c.rating_writer)||0) + (parseFloat(c.readability)||0) + (parseFloat(c.vastu)||0) + (parseFloat(c.shilpam)||0) + (parseFloat(c.innovation)||0);
          const composite = isVoiceCat ? 0.80 * vSum + 0.20 * sSum : 0.75 * sSum + 0.25 * vSum;
          return {
            performance_id: p.performance_id,
            episode_id: p.episode_id,
            _storytellerKey: nameKey(p.storyteller_name_telugu),
            _hasTitle: !!(display.storyTitle && display.storyTitle.trim()),
            ...display,
            criteria: c,
            vAvg: vSum / 4,
            sAvg: sSum / 6,
            composite,
            videoId: ep?.video_id || null,
            startSec: p?.story_start_sec || 0,
            startTime: p?.story_start_time || "",
            _tbPrimary: isVoiceCat ? vSum : sSum,
            _tbSecondary: isVoiceCat ? (parseFloat(c.modulation)||0) : (parseFloat(c.innovation)||0),
          };
        });
      entries.sort((a, b) => {
        if (b.composite !== a.composite) return b.composite - a.composite;
        if (b._tbPrimary !== a._tbPrimary) return b._tbPrimary - a._tbPrimary;
        if (b._tbSecondary !== a._tbSecondary) return b._tbSecondary - a._tbSecondary;
        // Within a true tie, prefer rows with a real story title
        if (a._hasTitle !== b._hasTitle) return a._hasTitle ? -1 : 1;
        return 0;
      });
      // Dedup ingestion artifacts: one (storyteller, episode) = one physical
      // performance, even if multiple `performances` rows exist for it.
      const seen = new Set();
      const deduped = [];
      for (const e of entries) {
        if (!e.episode_id || !e._storytellerKey) {
          deduped.push(e);
          continue;
        }
        const k = `${e._storytellerKey}|${e.episode_id}`;
        if (seen.has(k)) continue;
        seen.add(k);
        deduped.push(e);
      }
      result[section.category] = deduped.slice(0, 50);
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPerformances, shortlist, judges, episodesMap, seasons, selectedSeasonId, storiesMap, writersMap, storytellersMap]);

  // ---------- Judges tab: per-judge top-5 picks side-by-side ----------
  const judgesViewData = useMemo(() => {
    if (typeof window !== 'undefined') window.__jvdTrace = { activeTab, judgesLen: judges.length, scoresLen: judgeScores.length, perfsLen: allPerformances.length };
    if (activeTab !== "judges") return { groups: [] };
    if (judges.length === 0 || judgeScores.length === 0) return { groups: [] };
    const VOICE_CATS = new Set(["గళకళ", "తొలి కథనం - గళం"]);
    const WRITING_CATS = new Set(["కలం-గళం", "తొలి కథనం - కలం"]);
    const humans = judges.filter(
      (j) => (j.storyteller_english || "").toLowerCase() !== "kathakala agent"
    );
    const perCard = humans
      .map((j) => {
        const myScores = judgeScores.filter(
          (s) =>
            s.judge_storyteller_id === j.storyteller_id && isComplete(s)
        );
        const items = myScores
          .map((s) => {
            const perf = allPerformances.find(
              (p) => p.performance_id === s.performance_id
            );
            if (!perf) return null;
            const cat = perf.category;
            const v =
              (s.shruti || 0) +
              (s.modulation || 0) +
              (s.uccharana || 0) +
              (s.gati || 0);
            const st =
              (s.rating_story || 0) +
              (s.rating_writer || 0) +
              (s.readability || 0) +
              (s.vastu || 0) +
              (s.shilpam || 0) +
              (s.innovation || 0);
            const composite = VOICE_CATS.has(cat)
              ? 0.80 * v + 0.20 * st
              : 0.75 * st + 0.25 * v;
            const display = getPerfDisplay(perf.performance_id);
            return {
              performance_id: perf.performance_id,
              category: cat,
              storyTitle: display.storyTitle,
              writerName: display.writerName,
              storytellerName: display.storytellerName,
              composite,
              v,
              s: st,
              modulation: s.modulation,
              innovation: s.innovation,
            };
          })
          .filter(Boolean);
        // Primary: composite desc; tiebreaker: V (voice cats) / S (writing cats) desc; then mod / inn desc
        items.sort((a, b) => {
          if (b.composite !== a.composite) return b.composite - a.composite;
          const isVoice = VOICE_CATS.has(a.category);
          const ap = isVoice ? a.v : a.s;
          const bp = isVoice ? b.v : b.s;
          if (bp !== ap) return bp - ap;
          const at = VOICE_CATS.has(a.category) ? a.modulation : a.innovation;
          const bt = VOICE_CATS.has(b.category) ? b.modulation : b.innovation;
          return (bt || 0) - (at || 0);
        });
        const hasVoice = items.some((it) => VOICE_CATS.has(it.category));
        const hasWriting = items.some((it) => WRITING_CATS.has(it.category));
        const role =
          hasVoice && hasWriting
            ? "all"
            : hasVoice
            ? "voice"
            : hasWriting
            ? "writing"
            : "unknown";
        return {
          judge: j,
          top5: items.slice(0, 5),
          totalScored: items.length,
          role,
        };
      })
      .filter((c) => c.totalScored > 0);
    if (typeof window !== 'undefined') window.__jvdPerCard = perCard.map(c => ({ name: c.judge.storyteller_english, total: c.totalScored, role: c.role }));

    const roleOrder = { all: 0, voice: 1, writing: 2, unknown: 3 };
    perCard.sort(
      (a, b) =>
        roleOrder[a.role] - roleOrder[b.role] ||
        (a.judge.judge_sequence || 99) - (b.judge.judge_sequence || 99)
    );
    const groups = [
      {
        role: "all",
        label: "All-rounders",
        cards: perCard.filter((c) => c.role === "all"),
      },
      {
        role: "voice",
        label: "Voice-only",
        cards: perCard.filter((c) => c.role === "voice"),
      },
      {
        role: "writing",
        label: "Writing-only",
        cards: perCard.filter((c) => c.role === "writing"),
      },
    ].filter((g) => g.cards.length > 0);
    return { groups };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, judges, judgeScores, allPerformances, storiesMap, writersMap, storytellersMap]);

  // ---------- Render: Login ----------
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            జీడిగుంట పురస్కారాలు - Admin
          </h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter admin password"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg mb-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError("");
              }}
            />
            {passwordError && (
              <p className="text-red-500 text-sm mb-2">{passwordError}</p>
            )}
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- Render: Main Layout ----------
  return (
    <div className="min-h-screen bg-gray-100 px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-4 text-center">
          జీడిగుంట పురస్కారాలు - Admin
        </h1>

        {/* Messages */}
        {errorMsg && (
          <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-2 rounded mb-4 text-center">
            {errorMsg}
          </div>
        )}
        {successMsg && (
          <div className="bg-green-100 border border-green-300 text-green-700 px-4 py-2 rounded mb-4 text-center">
            {successMsg}
          </div>
        )}

        {/* Season Selector */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Season
              </label>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 bg-white"
                value={selectedSeasonId}
                onChange={(e) => setSelectedSeasonId(e.target.value)}
              >
                <option value="">-- Select Season --</option>
                {seasons.map((s) => (
                  <option key={s.season_id} value={String(s.season_id)}>
                    {s.name} ({s.status})
                  </option>
                ))}
              </select>
            </div>

            {selectedSeason && (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-500">
                  {selectedSeason.date_from} to {selectedSeason.date_to}
                </span>
                <span
                  className={`text-xs font-semibold px-2 py-1 rounded ${
                    selectedSeason.status === "open"
                      ? "bg-green-100 text-green-800"
                      : selectedSeason.status === "judging"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {selectedSeason.status}
                </span>
                {selectedSeason.status !== "open" && (
                  <button
                    onClick={() => handleChangeStatus("open")}
                    className="text-xs bg-green-100 text-green-700 hover:bg-green-200 px-2 py-1 rounded"
                  >
                    Open
                  </button>
                )}
                {selectedSeason.status !== "judging" && (
                  <button
                    onClick={() => handleChangeStatus("judging")}
                    className="text-xs bg-yellow-100 text-yellow-700 hover:bg-yellow-200 px-2 py-1 rounded"
                  >
                    Judging
                  </button>
                )}
                {selectedSeason.status !== "closed" && (
                  <button
                    onClick={() => handleChangeStatus("closed")}
                    className="text-xs bg-gray-200 text-gray-700 hover:bg-gray-300 px-2 py-1 rounded"
                  >
                    Close
                  </button>
                )}
              </div>
            )}
          </div>

          {/* New Season Form */}
          <details className="mt-4">
            <summary className="text-sm text-blue-600 cursor-pointer hover:underline">
              + New Season
            </summary>
            <div className="flex flex-wrap gap-3 items-end mt-2">
              <div>
                <label className="block text-xs text-gray-500">Name</label>
                <input
                  type="text"
                  className="border rounded px-2 py-1 text-sm"
                  placeholder="e.g., 2026"
                  value={newSeason.name}
                  onChange={(e) =>
                    setNewSeason({ ...newSeason, name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">From</label>
                <input
                  type="date"
                  className="border rounded px-2 py-1 text-sm"
                  value={newSeason.date_from}
                  onChange={(e) =>
                    setNewSeason({ ...newSeason, date_from: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">To</label>
                <input
                  type="date"
                  className="border rounded px-2 py-1 text-sm"
                  value={newSeason.date_to}
                  onChange={(e) =>
                    setNewSeason({ ...newSeason, date_to: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Status</label>
                <select
                  className="border rounded px-2 py-1 text-sm"
                  value={newSeason.status}
                  onChange={(e) =>
                    setNewSeason({ ...newSeason, status: e.target.value })
                  }
                >
                  <option value="open">open</option>
                  <option value="judging">judging</option>
                  <option value="closed">closed</option>
                </select>
              </div>
              <button
                onClick={handleCreateSeason}
                className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded hover:bg-blue-700 transition"
              >
                Create
              </button>
            </div>
          </details>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-1 mb-6 bg-white rounded-lg shadow-sm p-1 max-w-lg mx-auto">
          {[
            { key: "dashboard", label: "Dashboard" },
            { key: "shortlist", label: "Shortlist" },
            { key: "scores", label: "Scores" },
            { key: "results", label: "Results" },
            { key: "judges", label: "Judges" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition ${
                activeTab === tab.key
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* ==================== DASHBOARD TAB ==================== */}
        {activeTab === "dashboard" && !isLoading && (() => {
          const CAT_MAP = {
            "గళకళ": "galakala",
            "కలం-గళం": "kalam_galam",
            "తొలి కథనం - గళం": "toli_kathanam_galam",
            "తొలి కథనం - కలం": "toli_kathanam_kalam",
          };
          const CAT_LABELS = {
            galakala: "గళకళ",
            kalam_galam: "కలం-గళం",
            toli_kathanam_galam: "తొలి కథనం - గళం",
            toli_kathanam_kalam: "తొలి కథనం - కలం",
          };

          // Build per-category performance lists
          const catPerfIds = {};
          for (const section of AWARD_SECTIONS) {
            catPerfIds[section.category] = [
              ...new Set(
                shortlist
                  .filter((s) => {
                    const perf = allPerformances.find((p) => p.performance_id === s.performance_id);
                    return perf && CAT_MAP[perf.category] === section.category;
                  })
                  .map((s) => s.performance_id)
              ),
            ];
          }
          const totalPerfs = shortlist.length;

          // Build per-judge stats
          const judgeStats = judges.map((j) => {
            const jScores = judgeScores.filter((s) => s.judge_storyteller_id === j.storyteller_id);
            const complete = jScores.filter((s) => ALL_FIELDS.every((f) => s[f] !== null && s[f] !== undefined));
            const partial = jScores.filter((s) => {
              const filled = ALL_FIELDS.filter((f) => s[f] !== null && s[f] !== undefined).length;
              return filled > 0 && filled < 10;
            });

            // Per-category breakdown
            const catBreakdown = {};
            for (const section of AWARD_SECTIONS) {
              const perfIdsInCat = catPerfIds[section.category] || [];
              const catScores = jScores.filter((s) => perfIdsInCat.includes(s.performance_id));
              const catComplete = catScores.filter((s) => ALL_FIELDS.every((f) => s[f] !== null && s[f] !== undefined));
              catBreakdown[section.category] = {
                total: perfIdsInCat.length,
                scored: catComplete.length,
              };
            }

            return {
              ...j,
              total: totalPerfs,
              complete: complete.length,
              partial: partial.length,
              notStarted: totalPerfs - jScores.length,
              pct: totalPerfs > 0 ? Math.round((complete.length / totalPerfs) * 100) : 0,
              catBreakdown,
            };
          });

          // Overall stats
          const totalExpected = judges.length * totalPerfs;
          const totalComplete = judgeStats.reduce((sum, j) => sum + j.complete, 0);
          const overallPct = totalExpected > 0 ? Math.round((totalComplete / totalExpected) * 100) : 0;

          return (
            <div className="space-y-6">
              {/* Overall Progress */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Scoring Progress Overview</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-blue-700">{judges.length}</div>
                    <div className="text-sm text-blue-600">Judges</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-amber-700">{totalPerfs}</div>
                    <div className="text-sm text-amber-600">Performances</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-700">{totalComplete}</div>
                    <div className="text-sm text-green-600">of {totalExpected} Scores Done</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-purple-700">{overallPct}%</div>
                    <div className="text-sm text-purple-600">Overall Complete</div>
                  </div>
                </div>
                {/* Overall progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${overallPct}%`,
                      background: overallPct === 100 ? '#16a34a' : overallPct > 50 ? '#2563eb' : '#d97706',
                    }}
                  />
                </div>
              </div>

              {/* Per-Judge Progress */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50">
                  <h2 className="text-lg font-semibold text-gray-800">Judge-wise Progress</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-left">
                        <th className="px-3 py-2 border-b">#</th>
                        <th className="px-3 py-2 border-b">Judge</th>
                        <th className="px-3 py-2 border-b text-center">Complete</th>
                        <th className="px-3 py-2 border-b text-center">Partial</th>
                        <th className="px-3 py-2 border-b text-center">Not Started</th>
                        <th className="px-3 py-2 border-b min-w-[200px]">Progress</th>
                        {AWARD_SECTIONS.map((s) => (
                          <th key={s.category} className="px-3 py-2 border-b text-center whitespace-nowrap text-xs">
                            {CAT_LABELS[s.category]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {judgeStats.map((j, idx) => (
                        <tr key={j.storyteller_id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="px-3 py-2 border-b text-gray-500">{idx + 1}</td>
                          <td className="px-3 py-2 border-b font-medium">
                            <div>{j.storyteller_telugu}</div>
                            <div className="text-xs text-gray-500">{j.storyteller_english}</div>
                          </td>
                          <td className="px-3 py-2 border-b text-center">
                            <span className="inline-block bg-green-100 text-green-800 rounded-full px-2 py-0.5 text-xs font-semibold">
                              {j.complete}
                            </span>
                          </td>
                          <td className="px-3 py-2 border-b text-center">
                            {j.partial > 0 ? (
                              <span className="inline-block bg-yellow-100 text-yellow-800 rounded-full px-2 py-0.5 text-xs font-semibold">
                                {j.partial}
                              </span>
                            ) : (
                              <span className="text-gray-300">0</span>
                            )}
                          </td>
                          <td className="px-3 py-2 border-b text-center">
                            {j.notStarted > 0 ? (
                              <span className="inline-block bg-red-100 text-red-800 rounded-full px-2 py-0.5 text-xs font-semibold">
                                {j.notStarted}
                              </span>
                            ) : (
                              <span className="text-gray-300">0</span>
                            )}
                          </td>
                          <td className="px-3 py-2 border-b">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{
                                    width: `${j.pct}%`,
                                    background: j.pct === 100 ? '#16a34a' : j.pct > 50 ? '#2563eb' : j.pct > 0 ? '#d97706' : '#e5e7eb',
                                  }}
                                />
                              </div>
                              <span className="text-xs font-semibold w-10 text-right">{j.pct}%</span>
                            </div>
                          </td>
                          {AWARD_SECTIONS.map((s) => {
                            const cb = j.catBreakdown[s.category];
                            const catPct = cb.total > 0 ? Math.round((cb.scored / cb.total) * 100) : 0;
                            return (
                              <td key={s.category} className="px-3 py-2 border-b text-center">
                                <div className="text-xs font-semibold">{cb.scored}/{cb.total}</div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${catPct}%`,
                                      background: catPct === 100 ? '#16a34a' : catPct > 0 ? '#3b82f6' : '#e5e7eb',
                                    }}
                                  />
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                    {/* Totals row */}
                    <tfoot>
                      <tr className="bg-gray-100 font-semibold">
                        <td className="px-3 py-2" colSpan={2}>Total</td>
                        <td className="px-3 py-2 text-center text-green-700">{totalComplete}</td>
                        <td className="px-3 py-2 text-center text-yellow-700">
                          {judgeStats.reduce((sum, j) => sum + j.partial, 0)}
                        </td>
                        <td className="px-3 py-2 text-center text-red-700">
                          {judgeStats.reduce((sum, j) => sum + j.notStarted, 0)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-300 rounded-full h-3 overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{
                                  width: `${overallPct}%`,
                                  background: overallPct === 100 ? '#16a34a' : '#2563eb',
                                }}
                              />
                            </div>
                            <span className="text-xs w-10 text-right">{overallPct}%</span>
                          </div>
                        </td>
                        {AWARD_SECTIONS.map((s) => {
                          const catTotal = judgeStats.reduce((sum, j) => sum + j.catBreakdown[s.category].total, 0);
                          const catScored = judgeStats.reduce((sum, j) => sum + j.catBreakdown[s.category].scored, 0);
                          return (
                            <td key={s.category} className="px-3 py-2 text-center text-xs">
                              {catScored}/{catTotal}
                            </td>
                          );
                        })}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ==================== SHORTLIST TAB ==================== */}
        {activeTab === "shortlist" && !isLoading && (
          <div>
            {/* YouTube Player */}
            {playerVideoId && (
              <div className="bg-gray-900 rounded-lg shadow-sm overflow-hidden mb-4">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-800">
                  <span className="text-white text-sm font-medium">Video Preview</span>
                  <button
                    onClick={() => setPlayerVideoId(null)}
                    className="text-gray-400 hover:text-white text-lg leading-none"
                  >✕</button>
                </div>
                <div className="flex justify-center py-2">
                  <iframe
                    key={`${playerVideoId}-${playerStartSec}`}
                    width="640"
                    height="360"
                    src={`https://www.youtube.com/embed/${playerVideoId}?start=${playerStartSec}&autoplay=1`}
                    title="Episode Preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded"
                  />
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="flex flex-wrap gap-4 mb-3 text-xs text-gray-500 justify-center">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:"#1565c0"}}></span> Voice criteria (max 40)</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:"#e65100"}}></span> Story criteria (max 60)</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:"#4a148c"}}></span> Composite</span>
              <span>Agent AI Scores | గళకళ/తొలి-గళం = 80%V + 20%S | కలం-గళం/తొలి-కలం = 75%S + 25%V</span>
            </div>

            {/* Category Sub-Tabs */}
            <div className="flex justify-center gap-1 mb-4 flex-wrap">
              {AWARD_SECTIONS.map((section) => {
                const count = (shortlistByCategory[section.category] || []).length;
                return (
                  <button
                    key={section.category}
                    onClick={() => setShortlistCatTab(section.category)}
                    className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition ${
                      shortlistCatTab === section.category
                        ? "bg-green-700 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {section.title.split(" (")[0]} <span className="text-xs opacity-80">({count})</span>
                  </button>
                );
              })}
            </div>

            {/* Ranked shortlist table per category */}
            {AWARD_SECTIONS.map((section) => {
              if (shortlistCatTab !== section.category) return null;
              const entries = shortlistByCategory[section.category] || [];
              const isVoiceCat = section.category === "galakala" || section.category === "toli_kathanam_galam";
              return (
                <div key={section.category} className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
                  <div className={`px-4 py-3 border-b border-l-4 ${section.borderColor} flex justify-between items-center`}>
                    <h2 className="text-lg font-bold text-gray-800">
                      {section.title}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        ({isVoiceCat ? "80% Voice + 20% Story" : "75% Story + 25% Voice"})
                      </span>
                    </h2>
                    <span className="text-sm text-gray-500">{entries.length} entries</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="text-center">
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700">#</th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700">▶</th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Storyteller</th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Story</th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Writer</th>
                          {/* Voice headers */}
                          <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>శ్రుతి</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>మాడ్యు</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>ఉచ్చా</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>గతి</th>
                          <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#0d47a1"}}>V.Avg</th>
                          {/* Story headers */}
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>కథ</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>రచన</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>పఠన</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>వస్తు</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>శిల్పం</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>నూతన</th>
                          <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#bf360c"}}>S.Avg</th>
                          {/* Composite */}
                          <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#4a148c"}}>
                            {isVoiceCat ? "80V+20S" : "75S+25V"}
                          </th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.length === 0 && (
                          <tr><td colSpan={19} className="text-center py-6 text-gray-400">No shortlisted performances in this category.</td></tr>
                        )}
                        {entries.map((entry, idx) => {
                          const medalColors = ["#FEF3C7", "#F3F4F6", "#FED7AA"];
                          const bgColor = idx < 3 && entry.composite !== null ? medalColors[idx] : undefined;
                          const c = entry.criteria || {};
                          const fmtScore = (v) => v != null ? parseFloat(v).toFixed(1) : "--";
                          const scoreClass = (v) => {
                            if (v == null) return "text-gray-300";
                            const n = parseFloat(v);
                            if (n >= 9.0) return "text-green-700 font-bold";
                            if (n < 8.0) return "text-red-700";
                            return "text-gray-700";
                          };
                          return (
                            <tr key={entry.shortlist_id} className="border-b hover:bg-gray-50" style={bgColor ? { backgroundColor: bgColor } : {}}>
                              <td className="px-2 py-1.5 font-bold text-gray-600 text-center">{idx + 1}</td>
                              <td className="px-2 py-1.5 text-center">
                                {entry.videoId ? (
                                  <button
                                    onClick={() => { setPlayerVideoId(entry.videoId); setPlayerStartSec(entry.startSec || 0); }}
                                    className="bg-red-700 text-white rounded px-1.5 py-0.5 text-xs hover:bg-red-800"
                                    title={`Play at ${entry.startTime || '0:00'}`}
                                  >▶</button>
                                ) : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-2 py-1.5 font-semibold text-left max-w-[160px] truncate" title={entry.storytellerName}>{entry.storytellerName}</td>
                              <td className="px-2 py-1.5 text-left max-w-[140px] truncate text-gray-600" title={entry.storyTitle}>{entry.storyTitle}</td>
                              <td className="px-2 py-1.5 text-left max-w-[120px] truncate text-gray-500" title={entry.writerName}>{entry.writerName}</td>
                              {/* Voice scores */}
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.shruti)}`}>{fmtScore(c.shruti)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.modulation)}`}>{fmtScore(c.modulation)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.uccharana)}`}>{fmtScore(c.uccharana)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.gati)}`}>{fmtScore(c.gati)}</td>
                              <td className="px-2 py-1.5 text-center font-bold" style={{background:"#e3f2fd"}}>{entry.vAvg != null ? entry.vAvg.toFixed(2) : "--"}</td>
                              {/* Story scores */}
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.rating_story)}`}>{fmtScore(c.rating_story)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.rating_writer)}`}>{fmtScore(c.rating_writer)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.readability)}`}>{fmtScore(c.readability)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.vastu)}`}>{fmtScore(c.vastu)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.shilpam)}`}>{fmtScore(c.shilpam)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.innovation)}`}>{fmtScore(c.innovation)}</td>
                              <td className="px-2 py-1.5 text-center font-bold" style={{background:"#fff3e0"}}>{entry.sAvg != null ? entry.sAvg.toFixed(2) : "--"}</td>
                              {/* Composite */}
                              <td className="px-2 py-1.5 text-center font-bold text-sm" style={{background:"#f3e5f5"}}>{entry.composite != null ? entry.composite.toFixed(2) : "--"}</td>
                              <td className="px-2 py-1.5 text-center">
                                <button
                                  onClick={() => handleRemoveFromShortlist(entry.shortlist_id)}
                                  className="text-red-600 hover:text-red-800 text-xs font-medium"
                                >Remove</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Extended list: next 50 by agent score (not shortlisted) */}
                  {(() => {
                    const extended = extendedByCategory[section.category] || [];
                    if (extended.length === 0) return null;
                    return (
                      <details className="border-t border-gray-200">
                        <summary className="px-4 py-3 cursor-pointer font-semibold text-gray-700 bg-gray-50 hover:bg-gray-100 text-sm">
                          Next {extended.length} by agent score (not shortlisted)
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            — same tie-breaker as shortlist; judges excluded
                          </span>
                        </summary>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="text-center">
                                <th className="px-2 py-2 border-b bg-gray-100 text-gray-700">#</th>
                                <th className="px-2 py-2 border-b bg-gray-100 text-gray-700">▶</th>
                                <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Storyteller</th>
                                <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Story</th>
                                <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Writer</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>శ్రుతి</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>మాడ్యు</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>ఉచ్చా</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>గతి</th>
                                <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#0d47a1"}}>V.Avg</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>కథ</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>రచన</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>పఠన</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>వస్తు</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>శిల్పం</th>
                                <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>నూతన</th>
                                <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#bf360c"}}>S.Avg</th>
                                <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#4a148c"}}>
                                  {isVoiceCat ? "80V+20S" : "75S+25V"}
                                </th>
                                <th className="px-2 py-2 border-b bg-gray-100 text-gray-700">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {extended.map((entry, idx) => {
                                const c = entry.criteria || {};
                                const fmtScore = (v) => v != null ? parseFloat(v).toFixed(1) : "--";
                                const scoreClass = (v) => {
                                  if (v == null) return "text-gray-300";
                                  const n = parseFloat(v);
                                  if (n >= 9.0) return "text-green-700 font-bold";
                                  if (n < 8.0) return "text-red-700";
                                  return "text-gray-700";
                                };
                                return (
                                  <tr key={entry.performance_id} className="border-b hover:bg-gray-50">
                                    <td className="px-2 py-1.5 font-bold text-gray-600 text-center">{entries.length + idx + 1}</td>
                                    <td className="px-2 py-1.5 text-center">
                                      {entry.videoId ? (
                                        <button
                                          onClick={() => { setPlayerVideoId(entry.videoId); setPlayerStartSec(entry.startSec || 0); }}
                                          className="bg-red-700 text-white rounded px-1.5 py-0.5 text-xs hover:bg-red-800"
                                          title={`Play at ${entry.startTime || '0:00'}`}
                                        >▶</button>
                                      ) : <span className="text-gray-300">—</span>}
                                    </td>
                                    <td className="px-2 py-1.5 font-semibold text-left max-w-[160px] truncate" title={entry.storytellerName}>{entry.storytellerName}</td>
                                    <td className="px-2 py-1.5 text-left max-w-[140px] truncate text-gray-600" title={entry.storyTitle}>{entry.storyTitle}</td>
                                    <td className="px-2 py-1.5 text-left max-w-[120px] truncate text-gray-500" title={entry.writerName}>{entry.writerName}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.shruti)}`}>{fmtScore(c.shruti)}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.modulation)}`}>{fmtScore(c.modulation)}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.uccharana)}`}>{fmtScore(c.uccharana)}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.gati)}`}>{fmtScore(c.gati)}</td>
                                    <td className="px-2 py-1.5 text-center font-bold" style={{background:"#e3f2fd"}}>{entry.vAvg != null ? entry.vAvg.toFixed(2) : "--"}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.rating_story)}`}>{fmtScore(c.rating_story)}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.rating_writer)}`}>{fmtScore(c.rating_writer)}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.readability)}`}>{fmtScore(c.readability)}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.vastu)}`}>{fmtScore(c.vastu)}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.shilpam)}`}>{fmtScore(c.shilpam)}</td>
                                    <td className={`px-2 py-1.5 text-center ${scoreClass(c.innovation)}`}>{fmtScore(c.innovation)}</td>
                                    <td className="px-2 py-1.5 text-center font-bold" style={{background:"#fff3e0"}}>{entry.sAvg != null ? entry.sAvg.toFixed(2) : "--"}</td>
                                    <td className="px-2 py-1.5 text-center font-bold text-sm" style={{background:"#f3e5f5"}}>{entry.composite != null ? entry.composite.toFixed(2) : "--"}</td>
                                    <td className="px-2 py-1.5 text-center">
                                      <button
                                        onClick={() => handleAddToShortlist(entry.performance_id)}
                                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                      >Add</button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    );
                  })()}
                </div>
              );
            })}

            {/* Add to shortlist */}
            <details className="bg-white rounded-lg shadow-sm mb-6">
              <summary className="px-4 py-3 cursor-pointer font-semibold text-gray-800 bg-gray-50 rounded-t-lg hover:bg-gray-100">
                Add to Shortlist ({allPerformances.length} performances)
              </summary>
              <div className="p-4">
                <p className="text-xs text-gray-500 mb-3">Category is determined by the performance's own category field.</p>
                <div className="overflow-x-auto max-h-80 overflow-y-auto border rounded">
                  <table className="w-full text-sm border-collapse">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left border-b">Story</th>
                        <th className="px-3 py-2 text-left border-b">Writer</th>
                        <th className="px-3 py-2 text-left border-b">Storyteller</th>
                        <th className="px-3 py-2 text-left border-b">AI Rating</th>
                        <th className="px-3 py-2 border-b"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {allPerformances.map((p) => {
                        const story = storiesMap[p.story_id];
                        const writer = story ? writersMap[story.writer_id] : null;
                        const storyteller = storytellersMap[p.storyteller_id];
                        const alreadyShortlisted = shortlist.some((sl) => sl.performance_id === p.performance_id);
                        return (
                          <tr key={p.performance_id} className={`border-b hover:bg-gray-50 ${alreadyShortlisted ? "bg-green-50" : ""}`}>
                            <td className="px-3 py-1.5">{story?.title_telugu || story?.title_english || "--"}</td>
                            <td className="px-3 py-1.5">{writer?.name_telugu || writer?.name_english || "--"}</td>
                            <td className="px-3 py-1.5">{storyteller?.storyteller_telugu || p.storyteller_name_telugu || "--"}</td>
                            <td className="px-3 py-1.5 text-center">{p.rating_story != null ? Number(p.rating_story).toFixed(1) : "--"}</td>
                            <td className="px-3 py-1.5 text-center">
                              {alreadyShortlisted ? (
                                <span className="text-green-600 text-xs">Added</span>
                              ) : (
                                <button onClick={() => handleAddToShortlist(p.performance_id)} className="bg-blue-600 text-white text-xs px-3 py-1 rounded hover:bg-blue-700">Add</button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {allPerformances.length === 0 && (
                        <tr><td colSpan={5} className="text-center py-4 text-gray-400">No performances found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </details>

            {/* Auto-Shortlist */}
            <details className="bg-white rounded-lg shadow-sm">
              <summary className="px-4 py-3 cursor-pointer font-semibold text-gray-800 bg-gray-50 rounded-t-lg hover:bg-gray-100">
                Auto-Shortlist Top-10 (by AI Story Rating)
              </summary>
              <div className="p-4">
              <div className="flex gap-3 items-end mb-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Category</label>
                  <select
                    className="border rounded-md px-3 py-2 text-sm bg-white"
                    value={autoCategory}
                    onChange={(e) => setAutoCategory(e.target.value)}
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleAutoShortlistPreview}
                  className="bg-amber-600 text-white text-sm px-4 py-2 rounded hover:bg-amber-700 transition"
                >Preview Top-10</button>
              </div>

              {autoPreview && (
                <div className="border rounded-lg p-3 bg-amber-50">
                  <p className="text-sm font-medium mb-2">
                    Preview: {autoPreview.length} performances to shortlist
                  </p>
                  <table className="w-full text-sm border-collapse mb-3">
                    <thead>
                      <tr className="bg-amber-100">
                        <th className="px-2 py-1 text-left">#</th>
                        <th className="px-2 py-1 text-left">Story</th>
                        <th className="px-2 py-1 text-left">Storyteller</th>
                        <th className="px-2 py-1 text-center">AI Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {autoPreview.map((p, idx) => {
                        const story = storiesMap[p.story_id];
                        const storyteller = storytellersMap[p.storyteller_id];
                        return (
                          <tr key={p.performance_id} className="border-b">
                            <td className="px-2 py-1">{idx + 1}</td>
                            <td className="px-2 py-1">{story?.title_telugu || "--"}</td>
                            <td className="px-2 py-1">{storyteller?.storyteller_telugu || p.storyteller_name_telugu || "--"}</td>
                            <td className="px-2 py-1 text-center">{p.rating_story != null ? Number(p.rating_story).toFixed(1) : "--"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex gap-2">
                    <button
                      onClick={handleConfirmAutoShortlist}
                      className="bg-green-600 text-white text-sm px-4 py-2 rounded hover:bg-green-700"
                    >
                      Confirm & Add All
                    </button>
                    <button
                      onClick={() => setAutoPreview(null)}
                      className="bg-gray-300 text-gray-700 text-sm px-4 py-2 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              </div>
            </details>
          </div>
        )}

        {/* ==================== SCORES TAB ==================== */}
        {activeTab === "scores" && !isLoading && (
          <div>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-4">
              <div className="px-4 py-3 border-b bg-gray-50 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-800">
                  Judge Scores Matrix
                </h2>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={handleLockAgentRatings}
                    disabled={agentLockStatus === "locking"}
                    className={`text-sm px-4 py-1.5 rounded transition ${
                      agentLockStatus === "locked"
                        ? "bg-purple-100 text-purple-700 border border-purple-300"
                        : agentLockStatus === "locking"
                        ? "bg-gray-300 text-gray-500 cursor-wait"
                        : "bg-purple-600 text-white hover:bg-purple-700"
                    }`}
                    title="Copy AI ratings from performances table into judge_scores as the Kathakala Agent judge"
                  >
                    {agentLockStatus === "locking"
                      ? "Locking..."
                      : agentLockStatus === "locked"
                      ? `Agent Locked (${agentLockCount})`
                      : "Lock Agent Ratings"}
                  </button>
                  <button
                    onClick={handleExportCSV}
                    className="bg-green-600 text-white text-sm px-4 py-1.5 rounded hover:bg-green-700"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-3 py-2 border-b border-r">Story</th>
                      <th className="px-3 py-2 border-b border-r">Writer</th>
                      <th className="px-3 py-2 border-b border-r">
                        Storyteller
                      </th>
                      {judges.map((j) => (
                        <th
                          key={j.storyteller_id}
                          className="px-3 py-2 border-b border-r text-center whitespace-nowrap"
                        >
                          {j.storyteller_telugu || j.storyteller_english}
                        </th>
                      ))}
                      <th className="px-3 py-2 border-b text-center font-bold">
                        Avg Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const uniquePerfIds = [
                        ...new Set(shortlist.map((s) => s.performance_id)),
                      ];
                      if (uniquePerfIds.length === 0) {
                        return (
                          <tr>
                            <td
                              colSpan={3 + judges.length + 1}
                              className="text-center py-8 text-gray-400"
                            >
                              No shortlisted performances.
                            </td>
                          </tr>
                        );
                      }
                      return uniquePerfIds.map((perfId) => {
                        const display = getPerfDisplay(perfId);
                        const isExpanded = expandedPerfId === perfId;
                        const perfScores = judgeScores.filter(
                          (s) => s.performance_id === perfId
                        );
                        const completeTotals = perfScores
                          .map((r) => totalScore(r))
                          .filter((t) => t !== null);
                        const avgTotal =
                          completeTotals.length > 0
                            ? (
                                completeTotals.reduce((a, b) => a + b, 0) /
                                completeTotals.length
                              ).toFixed(2)
                            : "--";

                        return (
                          <React.Fragment key={perfId}>
                            <tr
                              className="border-b hover:bg-gray-50 cursor-pointer"
                              onClick={() =>
                                setExpandedPerfId(
                                  isExpanded ? null : perfId
                                )
                              }
                            >
                              <td className="px-3 py-2 border-r font-medium">
                                {isExpanded ? "▼" : "▶"}{" "}
                                {display.storyTitle}
                              </td>
                              <td className="px-3 py-2 border-r">
                                {display.writerName}
                              </td>
                              <td className="px-3 py-2 border-r">
                                {display.storytellerName}
                              </td>
                              {judges.map((j) => {
                                const jScore = perfScores.find(
                                  (s) => s.judge_storyteller_id === j.storyteller_id
                                );
                                const t = jScore ? totalScore(jScore) : null;
                                const complete = jScore
                                  ? isComplete(jScore)
                                  : false;
                                return (
                                  <td
                                    key={j.storyteller_id}
                                    className={`px-3 py-2 border-r text-center ${
                                      !complete ? "text-gray-400" : ""
                                    }`}
                                  >
                                    {t !== null ? t.toFixed(1) : "--"}
                                  </td>
                                );
                              })}
                              <td className="px-3 py-2 text-center font-bold">
                                {avgTotal}
                              </td>
                            </tr>

                            {/* Expanded detail rows */}
                            {isExpanded && (
                              <>
                                {/* Voice subtotal row */}
                                <tr className="bg-amber-50 text-xs">
                                  <td
                                    className="px-3 py-1 border-r font-semibold text-amber-800"
                                    colSpan={3}
                                  >
                                    Voice (
                                    {VOICE_FIELDS.map(
                                      (f) => CRITERIA_LABELS[f].telugu
                                    ).join(", ")}
                                    )
                                  </td>
                                  {judges.map((j) => {
                                    const jScore = perfScores.find(
                                      (s) => s.judge_storyteller_id === j.storyteller_id
                                    );
                                    const vs = jScore
                                      ? voiceScore(jScore)
                                      : null;
                                    return (
                                      <td
                                        key={j.storyteller_id}
                                        className="px-3 py-1 border-r text-center"
                                      >
                                        {vs !== null ? vs.toFixed(2) : "--"}
                                      </td>
                                    );
                                  })}
                                  <td className="px-3 py-1 text-center font-semibold">
                                    {(() => {
                                      const vals = perfScores
                                        .map((r) => voiceScore(r))
                                        .filter((v) => v !== null);
                                      return vals.length > 0
                                        ? (
                                            vals.reduce((a, b) => a + b, 0) /
                                            vals.length
                                          ).toFixed(2)
                                        : "--";
                                    })()}
                                  </td>
                                </tr>
                                {/* Individual voice criteria */}
                                {VOICE_FIELDS.map((f) => (
                                  <tr
                                    key={f}
                                    className="bg-amber-50/50 text-xs"
                                  >
                                    <td
                                      className="px-3 py-1 border-r pl-8"
                                      colSpan={3}
                                    >
                                      {CRITERIA_LABELS[f].telugu} (
                                      {CRITERIA_LABELS[f].english})
                                    </td>
                                    {judges.map((j) => {
                                      const jScore = perfScores.find(
                                        (s) => s.judge_storyteller_id === j.storyteller_id
                                      );
                                      const val = jScore
                                        ? parseFloat(jScore[f])
                                        : null;
                                      return (
                                        <td
                                          key={j.storyteller_id}
                                          className="px-3 py-1 border-r text-center"
                                        >
                                          {val !== null && !isNaN(val)
                                            ? val.toFixed(1)
                                            : "--"}
                                        </td>
                                      );
                                    })}
                                    <td className="px-3 py-1 text-center">
                                      {(() => {
                                        const avg = avgCriterion(
                                          perfScores,
                                          f
                                        );
                                        return avg !== null
                                          ? avg.toFixed(2)
                                          : "--";
                                      })()}
                                    </td>
                                  </tr>
                                ))}

                                {/* Story subtotal row */}
                                <tr className="bg-blue-50 text-xs">
                                  <td
                                    className="px-3 py-1 border-r font-semibold text-blue-800"
                                    colSpan={3}
                                  >
                                    Story (
                                    {STORY_FIELDS.map(
                                      (f) => CRITERIA_LABELS[f].telugu
                                    ).join(", ")}
                                    )
                                  </td>
                                  {judges.map((j) => {
                                    const jScore = perfScores.find(
                                      (s) => s.judge_storyteller_id === j.storyteller_id
                                    );
                                    const ss = jScore
                                      ? storyScore(jScore)
                                      : null;
                                    return (
                                      <td
                                        key={j.storyteller_id}
                                        className="px-3 py-1 border-r text-center"
                                      >
                                        {ss !== null ? ss.toFixed(2) : "--"}
                                      </td>
                                    );
                                  })}
                                  <td className="px-3 py-1 text-center font-semibold">
                                    {(() => {
                                      const vals = perfScores
                                        .map((r) => storyScore(r))
                                        .filter((v) => v !== null);
                                      return vals.length > 0
                                        ? (
                                            vals.reduce((a, b) => a + b, 0) /
                                            vals.length
                                          ).toFixed(2)
                                        : "--";
                                    })()}
                                  </td>
                                </tr>
                                {/* Individual story criteria */}
                                {STORY_FIELDS.map((f) => (
                                  <tr
                                    key={f}
                                    className="bg-blue-50/50 text-xs"
                                  >
                                    <td
                                      className="px-3 py-1 border-r pl-8"
                                      colSpan={3}
                                    >
                                      {CRITERIA_LABELS[f].telugu} (
                                      {CRITERIA_LABELS[f].english})
                                    </td>
                                    {judges.map((j) => {
                                      const jScore = perfScores.find(
                                        (s) => s.judge_storyteller_id === j.storyteller_id
                                      );
                                      const val = jScore
                                        ? parseFloat(jScore[f])
                                        : null;
                                      return (
                                        <td
                                          key={j.storyteller_id}
                                          className="px-3 py-1 border-r text-center"
                                        >
                                          {val !== null && !isNaN(val)
                                            ? val.toFixed(1)
                                            : "--"}
                                        </td>
                                      );
                                    })}
                                    <td className="px-3 py-1 text-center">
                                      {(() => {
                                        const avg = avgCriterion(
                                          perfScores,
                                          f
                                        );
                                        return avg !== null
                                          ? avg.toFixed(2)
                                          : "--";
                                      })()}
                                    </td>
                                  </tr>
                                ))}
                              </>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ==================== RESULTS TAB ==================== */}
        {activeTab === "results" && !isLoading && (
          <div>
            {/* Judge Selector */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="flex flex-wrap gap-4 items-center">
                <label className="text-sm font-semibold text-gray-700">
                  View Rankings By:
                </label>
                <select
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                  value={resultsJudge}
                  onChange={(e) => setResultsJudge(e.target.value)}
                >
                  <option value="all">All Judges (Averaged)</option>
                  <option value="ai">Kathakala Agent (AI Scores)</option>
                  {judges.map((j) => (
                    <option key={j.storyteller_id} value={j.storyteller_id}>
                      {j.storyteller_telugu || j.storyteller_english}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">
                  {resultsJudge === "all"
                    ? "Composite from averaged judge criteria"
                    : resultsJudge === "ai"
                    ? "AI-generated scores from Kathakala system"
                    : "Individual judge's scores only"}
                </span>
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:"#1565c0"}}></span> Voice criteria</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:"#e65100"}}></span> Story criteria</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{background:"#4a148c"}}></span> Composite</span>
                <span>గళకళ/తొలి-గళం = 80% Voice + 20% Story | కలం-గళం/తొలి-కలం = 75% Story + 25% Voice</span>
              </div>
            </div>

            {/* Video Player for Results */}
            {playerVideoId && (
              <div className="bg-gray-900 rounded-lg shadow-sm overflow-hidden mb-4">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-800">
                  <span className="text-white text-sm font-medium">Video Preview</span>
                  <button
                    onClick={() => setPlayerVideoId(null)}
                    className="text-gray-400 hover:text-white text-lg leading-none"
                  >✕</button>
                </div>
                <div className="flex justify-center py-2">
                  <iframe
                    key={`${playerVideoId}-${playerStartSec}`}
                    width="640"
                    height="360"
                    src={`https://www.youtube.com/embed/${playerVideoId}?start=${playerStartSec}&autoplay=1`}
                    title="Episode Preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded"
                  />
                </div>
              </div>
            )}

            {AWARD_SECTIONS.map((section) => {
              const ranked = resultsData[section.category] || [];
              const isVoiceCat = section.category === "galakala" || section.category === "toli_kathanam_galam";
              return (
                <div
                  key={section.category}
                  className="bg-white rounded-lg shadow-sm overflow-hidden mb-6"
                >
                  <div
                    className={`px-4 py-3 border-b border-l-4 ${section.borderColor}`}
                  >
                    <h2 className="text-lg font-bold text-gray-800">
                      {section.title}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        ({isVoiceCat ? "80% Voice + 20% Story" : "75% Story + 25% Voice"})
                      </span>
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="text-center">
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700" rowSpan={1}>#</th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700">▶</th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Storyteller</th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Story</th>
                          <th className="px-2 py-2 border-b bg-gray-100 text-gray-700 text-left">Writer</th>
                          {/* Voice criteria - blue headers */}
                          <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>శ్రుతి</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>మాడ్యు</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>ఉచ్చా</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#1565c0"}}>గతి</th>
                          <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#0d47a1"}}>V.Avg</th>
                          {/* Story criteria - orange headers */}
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>కథ</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>రచన</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>పఠన</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>వస్తు</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>శిల్పం</th>
                          <th className="px-2 py-2 border-b text-white" style={{background:"#e65100"}}>నూతన</th>
                          <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#bf360c"}}>S.Avg</th>
                          {/* Composite */}
                          <th className="px-2 py-2 border-b text-white font-bold" style={{background:"#4a148c"}}>
                            {isVoiceCat ? "80V+20S" : "75S+25V"}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {ranked.length === 0 && (
                          <tr>
                            <td
                              colSpan={18}
                              className="text-center py-6 text-gray-400"
                            >
                              No shortlisted performances in this category.
                            </td>
                          </tr>
                        )}
                        {ranked.map((entry, idx) => {
                          const medalColors = ["#FEF3C7", "#F3F4F6", "#FED7AA"];
                          const bgColor =
                            idx < 3 && entry.composite !== null
                              ? medalColors[idx]
                              : undefined;
                          const c = entry.criteria || {};
                          const fmtScore = (v) => v != null ? parseFloat(v).toFixed(1) : "--";
                          const scoreClass = (v) => {
                            if (v == null) return "text-gray-300";
                            const n = parseFloat(v);
                            if (n >= 9.0) return "text-green-700 font-bold";
                            if (n < 8.0) return "text-red-700";
                            return "text-gray-700";
                          };

                          return (
                            <tr
                              key={entry.performance_id}
                              className="border-b hover:bg-gray-50"
                              style={bgColor ? { backgroundColor: bgColor } : {}}
                            >
                              <td className="px-2 py-1.5 font-bold text-gray-600 text-center">
                                {idx + 1}
                              </td>
                              <td className="px-2 py-1.5 text-center">
                                {entry.videoId ? (
                                  <button
                                    onClick={() => {
                                      setPlayerVideoId(entry.videoId);
                                      setPlayerStartSec(entry.startSec || 0);
                                    }}
                                    className="bg-red-700 text-white rounded px-1.5 py-0.5 text-xs hover:bg-red-800"
                                    title="Play video"
                                  >▶</button>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 font-semibold text-left max-w-[160px] truncate" title={entry.storytellerName}>
                                {entry.storytellerName}
                              </td>
                              <td className="px-2 py-1.5 text-left max-w-[140px] truncate text-gray-600" title={entry.storyTitle}>
                                {entry.storyTitle}
                              </td>
                              <td className="px-2 py-1.5 text-left max-w-[120px] truncate text-gray-500" title={entry.writerName}>
                                {entry.writerName}
                              </td>
                              {/* Voice scores */}
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.shruti)}`}>{fmtScore(c.shruti)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.modulation)}`}>{fmtScore(c.modulation)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.uccharana)}`}>{fmtScore(c.uccharana)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.gati)}`}>{fmtScore(c.gati)}</td>
                              <td className="px-2 py-1.5 text-center font-bold" style={{background:"#e3f2fd"}}>
                                {entry.voiceScoreVal !== null ? entry.voiceScoreVal.toFixed(2) : "--"}
                              </td>
                              {/* Story scores */}
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.rating_story)}`}>{fmtScore(c.rating_story)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.rating_writer)}`}>{fmtScore(c.rating_writer)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.readability)}`}>{fmtScore(c.readability)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.vastu)}`}>{fmtScore(c.vastu)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.shilpam)}`}>{fmtScore(c.shilpam)}</td>
                              <td className={`px-2 py-1.5 text-center ${scoreClass(c.innovation)}`}>{fmtScore(c.innovation)}</td>
                              <td className="px-2 py-1.5 text-center font-bold" style={{background:"#fff3e0"}}>
                                {entry.storyScoreVal !== null ? entry.storyScoreVal.toFixed(2) : "--"}
                              </td>
                              {/* Composite */}
                              <td className="px-2 py-1.5 text-center font-bold text-sm" style={{background:"#f3e5f5"}}>
                                {entry.composite !== null
                                  ? entry.composite.toFixed(2)
                                  : "--"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ==================== JUDGES TAB ==================== */}
        {activeTab === "judges" && !isLoading && (
          <div>
            <div className="bg-white rounded-lg shadow-sm p-3 mb-4 text-xs text-gray-600">
              Top-5 picks per judge using their own composite
              (గళకళ/తొలి-గళం = 80%V + 20%S, కలం-గళం/తొలి-కలం = 75%S + 25%V).
              Tie-breaker: V-sum or S-sum → modulation or innovation.
              Grouped by specialization.
            </div>
            {judgesViewData.groups.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6 text-center text-gray-400">
                No judge scores loaded for this season.
              </div>
            )}
            {judgesViewData.groups.map((group) => (
              <div key={group.role} className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1">
                  {group.label}{" "}
                  <span className="text-xs font-normal text-gray-500">
                    ({group.cards.length} judge{group.cards.length === 1 ? "" : "s"})
                  </span>
                </h3>
                <div className="grid gap-3" style={{gridTemplateColumns: `repeat(${Math.max(1, Math.min(group.cards.length, 3))}, minmax(0, 1fr))`}}>
                  {group.cards.map(({ judge, top5, totalScored, role }) => (
                    <div
                      key={judge.storyteller_id}
                      className="bg-white rounded-lg shadow-sm overflow-hidden"
                    >
                      <div
                        className={`px-3 py-2 border-b border-l-4 ${
                          role === "all"
                            ? "border-purple-500"
                            : role === "voice"
                            ? "border-blue-500"
                            : "border-orange-500"
                        }`}
                      >
                        <div className="text-sm font-bold text-gray-800 truncate" title={judge.storyteller_telugu || judge.storyteller_english}>
                          {judge.storyteller_telugu || judge.storyteller_english}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {judge.storyteller_english}
                          {judge.judge_sequence ? ` · J${judge.judge_sequence}` : ""} · {totalScored} scored
                        </div>
                      </div>
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-gray-50 text-gray-600">
                            <th className="px-2 py-1 border-b text-center w-8">#</th>
                            <th className="px-2 py-1 border-b text-left">Storyteller</th>
                            <th className="px-2 py-1 border-b text-left">Story</th>
                            <th className="px-2 py-1 border-b text-left">Writer</th>
                            <th className="px-2 py-1 border-b text-center" style={{background:"#f3e5f5"}}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {top5.length === 0 && (
                            <tr><td colSpan={5} className="px-2 py-3 text-center text-gray-400">No complete scores.</td></tr>
                          )}
                          {top5.map((item, idx) => {
                            const medalBg = ["#FEF3C7", "#F3F4F6", "#FED7AA"][idx];
                            return (
                              <tr
                                key={item.performance_id}
                                className="border-b"
                                style={medalBg ? { backgroundColor: medalBg } : {}}
                              >
                                <td className="px-2 py-1 text-center font-bold text-gray-700">{idx + 1}</td>
                                <td className="px-2 py-1 max-w-[120px] truncate" title={item.storytellerName}>{item.storytellerName || "—"}</td>
                                <td className="px-2 py-1 max-w-[120px] truncate text-gray-700" title={item.storyTitle}>{item.storyTitle || "—"}</td>
                                <td className="px-2 py-1 max-w-[100px] truncate text-gray-500" title={item.writerName}>{item.writerName || "—"}</td>
                                <td className="px-2 py-1 text-center font-bold" style={{background:"#f3e5f5"}}>
                                  {item.composite.toFixed(2)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PuraskaraluAdmin;
